/**
 * How the Jobs screens draw a job's states — tones and row tints, the list's
 * address in the URL — kept out of the components so they stay about layout.
 */
import { z } from 'zod'
import type { BillingState, JobsQueryInput, NoticeState, ShownStatus } from '@/core/jobs'
import { billingStates, jobSortKeys, quickFilters, shownStatuses, whenFilters } from '@/core/jobs'
import type { Tone } from '@/features/shared/components/tone-pill'

export const statusTone: Record<ShownStatus, Tone> = {
  draft: 'neutral',
  scheduled: 'info',
  completed: 'success',
  cancelled: 'neutral',
}

export const billingTone: Record<BillingState, Tone> = {
  ready: 'pending',
  awaiting: 'pending',
  overdue: 'danger',
  draft: 'neutral',
  paid: 'success',
  written_off: 'neutral',
  monthly: 'neutral',
  after_visit: 'neutral',
  none: 'neutral',
}

/** Rows are tinted by status (rule 4): scheduled faint blue, completed faint green, cancelled and draft grey. */
export const rowTint: Record<ShownStatus, string> = {
  draft: 'bg-muted/60 text-muted-foreground',
  scheduled: 'bg-primary/[0.04]',
  completed: 'bg-success-surface/50',
  cancelled: 'bg-muted/60 text-muted-foreground',
}

export const noticeTone: Record<NoticeState, string> = {
  sent: 'text-success-strong',
  pending: 'text-muted-foreground',
  off: 'text-muted-foreground',
}

/**
 * The list's filters live in the address, so Back from a job returns to the
 * same page, and a filtered list can be shared as a link. A value the list
 * doesn't know (an old or hand-edited link) is dropped, not a broken screen.
 */
export const jobsSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  quick: z.enum(quickFilters).optional().catch(undefined),
  status: z.array(z.enum(shownStatuses)).optional().catch(undefined),
  clients: z.array(z.string()).optional().catch(undefined),
  locations: z.array(z.string()).optional().catch(undefined),
  cleaners: z.array(z.string()).optional().catch(undefined),
  workTypes: z.array(z.string()).optional().catch(undefined),
  billing: z.array(z.enum(billingStates)).optional().catch(undefined),
  when: z.enum(whenFilters).optional().catch(undefined),
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  sort: z.enum(jobSortKeys).optional().catch(undefined),
  dir: z.enum(['asc', 'desc']).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  size: z.number().int().optional().catch(undefined),
  view: z.enum(['deleted']).optional().catch(undefined),
})
export type JobsSearch = z.infer<typeof jobsSearchSchema>

export const PAGE_SIZES = [12, 25, 50] as const

export function queryFromSearch(search: JobsSearch): JobsQueryInput {
  return {
    q: search.q ?? '',
    quick: search.quick ?? null,
    status: search.status ?? [],
    clients: search.clients ?? [],
    locations: search.locations ?? [],
    cleaners: search.cleaners ?? [],
    workTypes: search.workTypes ?? [],
    billing: search.billing ?? [],
    when: search.when ?? null,
    from: search.from ?? null,
    to: search.to ?? null,
    sort: search.sort ? { key: search.sort, dir: search.dir ?? 'asc' } : null,
    page: (search.page ?? 1) - 1,
    pageSize: search.size ?? PAGE_SIZES[0],
  }
}

/** Anything narrowing the list — what "Clear all" clears. */
export function isFiltered(search: JobsSearch): boolean {
  return Boolean(
    search.q ||
      search.quick ||
      search.when ||
      [
        search.status,
        search.clients,
        search.locations,
        search.cleaners,
        search.workTypes,
        search.billing,
      ].some((list) => list && list.length > 0),
  )
}

export function clearedSearch(search: JobsSearch): JobsSearch {
  return { sort: search.sort, dir: search.dir, size: search.size }
}
